import { ArrowLeftRight, Clock3, HelpCircle, LoaderCircle, LockKeyhole, RefreshCw, UnlockKeyhole, UserCheck } from 'lucide-react'

import { Button } from '@krp/ui-kit'

import { QueryState } from '@krp/sale-ui'
import '../../../shared/ui/aqsi.css'
import type { RmkActions, RmkVm } from '../model/types'
import { FeedbackHelp } from './FeedbackHelp'
import { OperationPanel } from './OperationPanel'

/**
 * Порог смены перед продажей — как в старом РМК: пока смена закрыта, продажа
 * не открывается, а предлагается открыть смену (чек вне смены касса не
 * напечатает, и узнать об этом после оплаты — худший момент). Смену, открытую
 * на самой кассе, сначала принимают. Пока идёт команда открытия — экран ожидания
 * (`OpeningScreen`), а не этот порог. Вид цены здесь не выбирается (RP-FB22: он выбран
 * до «Открыть смену»; сменить его между чеками можно в «Действиях»). Проверка одна — «Проверить»:
 * она и читает смену у кабинета, и спрашивает кассу.
 */
export function ShiftGate({ vm, actions, hideOperation = false }: { vm: RmkVm; actions: RmkActions; hideOperation?: boolean }) {
  const mine = vm.mine
  if (mine === null) return null
  const shift = vm.shift
  const commands = vm.shiftCommands
  const closing = vm.operation?.kind === 'close' && !vm.operation.canDismiss
  const needsAttention = closing && vm.operation?.tone !== 'neutral'
  const leaving = shift?.isOpen === false && !vm.operation?.manualRequired &&
    (mine.shiftNumber !== null || (vm.operation?.kind === 'close' && vm.operation.tone === 'success'))
  const title =
    shift?.isOpen === false
      ? 'Смена закрыта'
      : commands.canAdopt
        ? 'Смена открыта на кассе вне программы'
        : shift?.isOpen === true
          ? 'Смена открыта'
          : 'Состояние смены не подтверждено'
  const checking = shift?.query.refreshing === true || vm.working !== null
  return (
    <section className="aqsi-screen rmk-gate" aria-labelledby="rmk-gate-title">
      <header className="aqsi-toolbar">
        <h2 id="rmk-gate-title">
          <UserCheck size={18} aria-hidden="true" /> {mine.registerName}
        </h2>
      </header>
      <p className="aqsi-caption">
        {vm.source.name}
        {vm.source.alternatives > 1 && (
          <>
            {' '}
            <Button variant="ghost" icon={<ArrowLeftRight size={14} aria-hidden="true" />} actionId="aqsi.rmk.other-source" onClick={actions.switchSource}>
              Другой источник
            </Button>
          </>
        )}
      </p>
      <FeedbackHelp help={vm.feedbackHelp} onIncidents={actions.openIncidents} />

      <section className="aqsi-panel rmk-gate__shift" aria-label="Смена кассы">
        <h3>
          {needsAttention ? <HelpCircle size={18} aria-hidden="true" /> : closing && !vm.operation?.checking && !vm.operation?.reconciling && vm.operation?.phase !== 'sending' ? <Clock3 size={18} aria-hidden="true" /> : closing || leaving ? <LoaderCircle className="aqsi-spinner" size={18} aria-hidden="true" /> : shift?.isOpen === true ? <UnlockKeyhole size={18} aria-hidden="true" /> : <LockKeyhole size={18} aria-hidden="true" />}
          {needsAttention ? 'Закрытие смены требует внимания' : closing ? 'Закрываем смену' : leaving ? 'Смена закрыта' : title}
        </h3>
        {shift !== null && <QueryState requestIdHidden query={shift.query} what="Смена" emptyText="Смена не прочитана" onRetry={actions.readShift} />}
        <p className="aqsi-card__text">
          {leaving
            ? 'Смена закрыта. Освобождаем рабочее место и возвращаем к выбору кассы.'
            : needsAttention
              ? 'Закрытие смены не подтверждено. Проверьте ответ кабинета ниже и состояние терминала. Повторная команда автоматически не отправляется.'
              : closing
              ? 'Ждём подтверждения закрытия кассой. Если аппарат просит подтверждение, выполните его на экране кассы.'
              : commands.canAdopt
                ? 'Смену открыли на самой кассе. Примите её, чтобы продавать и потом закрыть её отсюда.'
                : shift?.isOpen === false
                  ? 'Продажа начинается со смены: пока она закрыта, касса не напечатает чек.'
                  : 'Касса ещё не подтвердила состояние смены. Проверьте смену — команды до этого недоступны.'}
        </p>
        {shift !== null && shift.message !== '' && !closing && !leaving && <p className="aqsi-card__text">{shift.message}</p>}
        {!closing && !leaving && <div className="aqsi-card__actions rmk-gate__actions">
          {commands.canOpen && (
            <Button variant="primary" actionId="aqsi.rmk.open-shift" disabled={vm.busy} onClick={actions.openShift}>
              Открыть смену
            </Button>
          )}
          {commands.canAdopt && (
            <Button variant="primary" actionId="aqsi.rmk.continue-shift" disabled={vm.busy} onClick={actions.adoptShift}>
              Принять смену
            </Button>
          )}
          <Button
            icon={checking ? <LoaderCircle className="aqsi-spinner" size={14} aria-hidden="true" /> : <RefreshCw size={14} aria-hidden="true" />}
            busy={checking}
            actionId="aqsi.rmk.check-shift"
            onClick={() => {
              actions.readShift()
              actions.checkLink(mine.deviceId)
            }}
          >
            Проверить
          </Button>
        </div>}
        {vm.working !== null && (
          <p className="aqsi-card__text" role="status">
            {vm.working.text}
          </p>
        )}
        {commands.blockedReason !== null && !commands.canAdopt && !closing && !leaving && <p className="aqsi-card__text">{commands.blockedReason}</p>}
      </section>

      {!hideOperation && vm.operation !== null && (closing || vm.operation.manualRequired || vm.operation.tone === 'warning' || vm.operation.tone === 'danger') && (
        <OperationPanel
          operation={vm.operation}
          onCheck={actions.checkOperation}
          onReconcile={actions.reconcileOperation}
          onDismiss={actions.dismissOperation}
        />
      )}
    </section>
  )
}
